import { Role } from '@/acl'

export type Access = 'R' | 'W'
export type AllowedRoleTuple = [Role, Access]

export type AllowedRolesByPath = Record<
  string,
  Array<[keyof typeof Role, Access]>
>

/**
 * Data generated from the Excel ACL matrix.
 *
 * Keys are in format "module::path" (e.g., "ขาย (Sales)::/sale/sale1010")
 * NOTE: This is intentionally plain JSON data (no `Role.X` references).
 * We re-hydrate it into `[Role, 'R'|'W']` tuples at runtime.
 */
import allowedRolesByPathJson from './menu-allowed-roles.json'

const allowedRolesByPath =
  allowedRolesByPathJson as unknown as AllowedRolesByPath

/**
 * Get allowed roles for a specific module and path combination.
 * This is used for menu visibility - each module shows only its own roles.
 *
 * @param module - The exact module name from JSON (e.g., "ขาย (Sales)", "คลังสินค้า (Warehouse)")
 * @param menuPath - The menu path (e.g., "/warehouse/inv2215")
 * @returns Array of role tuples for this specific module, or undefined if not found
 *
 * @example
 * const roles = getAllowedRolesForModulePath("ขาย (Sales)", "/warehouse/inv2215")
 * // Returns only Sales module roles: [[Role.SALES_OFFICER, 'R'], [Role.SALES_SUPERVISOR, 'W']]
 */
export function getAllowedRolesForModulePath(
  module: string,
  menuPath: string
): AllowedRoleTuple[] | undefined {
  const key = `${module}::${menuPath}`
  const raw = allowedRolesByPath[key]
  if (!raw) return undefined

  return raw
    .map(([roleKey, access]) => {
      const role = Role[roleKey]
      if (!role) return undefined
      return [role, access] as const
    })
    .filter((t): t is AllowedRoleTuple => Boolean(t))
}

/**
 * Get allowed roles for a menu path by searching across all modules.
 * Since the JSON keys are "module::path", we need to find all entries
 * that end with "::path" and aggregate the roles.
 *
 * This is used for route guards - allowing access if user has permission from ANY module.
 *
 * @param menuPath - The menu path (e.g., "/warehouse/inv2215")
 * @returns Aggregated array of role tuples from all modules, or undefined if not found
 *
 * @example
 * const roles = getAllowedRolesForPath("/warehouse/inv2215")
 * // Returns combined roles from Sales + Warehouse + QC modules
 */
export function getAllowedRolesForPath(
  menuPath: string
): AllowedRoleTuple[] | undefined {
  const roleMap = new Map<Role, Access>()

  // Find all module::path entries that match this path
  for (const [key, tuples] of Object.entries(allowedRolesByPath)) {
    // Check if key ends with "::menuPath"
    if (key.endsWith(`::${menuPath}`)) {
      // Aggregate roles from this module
      for (const [roleKey, access] of tuples) {
        const role = Role[roleKey]
        if (!role) continue

        // If role already exists, keep 'W' over 'R'
        const existing = roleMap.get(role)
        if (!existing || existing === 'R') {
          roleMap.set(role, access)
        }
      }
    }
  }

  if (roleMap.size === 0) return undefined

  return Array.from(roleMap.entries())
}

export function hasAllowedRolesForPath(menuPath: string): boolean {
  // Check if any key ends with "::menuPath"
  return Object.keys(allowedRolesByPath).some((key) =>
    key.endsWith(`::${menuPath}`)
  )
}
